export const EMPTY_SENTINEL = Infinity;
export const BASELINE_COMPARISON_TOOLTIP =
  "Pattern median vs audience baseline. Can be skewed when pattern videos are from large creators (who tend to have higher counts).";
export const OUTLIER_TOOLTIP =
  "How videos in this pattern perform vs each creator's own average. Outlier per creator; less skewed by creator size than vs avg.";

export const formatNumber = (num) => {
  if (num == null) return "—";
  const n = Math.round(Number(num));
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
};

export const formatRatioDisplay = (ratio) => {
  if (ratio == null || ratio === 0) return null;
  const isAbove = ratio >= 1;
  let text;
  // Above +1000%: show multiplier e.g. x11
  if (isAbove && ratio > 11) {
    const label = Number.isInteger(ratio) ? String(ratio) : ratio.toFixed(1).replace(/\.0$/, "");
    text = `x${label}`;
  } else if (isAbove) {
    const pct = ((ratio - 1) * 100).toFixed(0);
    text = `+${pct}%`;
  } else {
    const pct = ((1 - ratio) * 100).toFixed(0);
    text = `-${pct}%`;
  }
  return { text, above: isAbove };
};

/**
 * Compact ratio display for verdict cards: uses multiplier (e.g. 27x) when ratio >= 2,
 * otherwise percentage. Returns { text, above }.
 */
export function formatRatioCompact(ratio) {
  if (ratio == null || ratio === 0) return null;
  const isAbove = ratio >= 1;
  let text;
  if (isAbove && ratio >= 2) {
    const mult = ratio >= 10 ? Math.round(ratio) : ratio.toFixed(1).replace(/\.0$/, "");
    text = `${mult}x`;
  } else if (isAbove) {
    const pct = ((ratio - 1) * 100).toFixed(0);
    text = `+${pct}%`;
  } else {
    const pct = ((1 - ratio) * 100).toFixed(0);
    text = `-${pct}%`;
  }
  return { text, above: isAbove };
}

/** Tailwind classes for ratio/outlier coloring. */
export const RATIO_COLOR_CLASSES = {
  positive: "text-green-600 dark:text-green-400",
  negative: "text-red-500 dark:text-red-400",
  muted: "text-theme-text opacity-40",
};

/**
 * Return the color class for a ratio (e.g. vs baseline or outlier).
 * Green when ratio > 1; red when ratio < 1; muted when null or ratio === 1 (+0%).
 */
export function getRatioColorClass(ratio) {
  if (ratio == null) return RATIO_COLOR_CLASSES.muted;
  if (ratio > 1) return RATIO_COLOR_CLASSES.positive;
  if (ratio < 1) return RATIO_COLOR_CLASSES.negative;
  return RATIO_COLOR_CLASSES.muted; // ratio === 1 (+0%)
}

export const isViewsEmpty = (v) =>
  v == null || v === "" || (typeof Number(v) === "number" && (Number.isNaN(Number(v)) || Number(v) === 0));

/**
 * For sidebar display: if pattern label starts with the category name, strip it
 * so we don't repeat "Content" in "Content Type" when already under Content.
 */
export function patternLabelInSidebar(label, categoryLabel) {
  if (!label || !categoryLabel) return label;
  const prefix = `${categoryLabel} `;
  if (label.slice(0, prefix.length).toLowerCase() === prefix.toLowerCase()) {
    return label.slice(prefix.length).trim();
  }
  return label;
}

/** Color tint for verdict labels: red (bad), yellow (neutral), green (good), white (muted). */
export const LABEL_TINT_CLASSES = {
  negative: "text-red-500 dark:text-red-400",
  neutral: "text-yellow-500 dark:text-yellow-400",
  positive: "text-green-600 dark:text-green-400",
  muted: "text-theme-text opacity-60",
};

/**
 * Get a human-readable label for Reach based on views vs audience median ratio.
 * ratio: pattern median views / audience median views (1.0 = typical).
 * Uses distinct labels/emojis from Creator Impact.
 */
export function getReachLabel(ratio) {
  if (ratio == null || Number.isNaN(ratio)) return null;
  const r = Number(ratio);
  if (r <= 0.2) return { label: "Very weak", emoji: "📉", tint: "negative" };
  if (r <= 0.5) return { label: "Weak", emoji: "⬇️", tint: "negative" };
  if (r <= 0.85) return { label: "Below typical", emoji: "↘️", tint: "neutral" };
  if (r <= 1.05) return { label: "Typical", emoji: "➡️", tint: "muted" };
  if (r <= 1.3) return { label: "Slight lift", emoji: "↗️", tint: "positive" };
  if (r <= 2.5) return { label: "Outperforms", emoji: "📈", tint: "positive" };
  return { label: "Massive reach", emoji: "📊", tint: "positive" };
}

/**
 * Get a human-readable label for Creator Impact based on composite outlier ratio.
 * ratio: pattern median vs creator typical (1.0 = typical, 2.0 = 2x).
 * Uses distinct labels/emojis from Reach.
 */
export function getCreatorImpactLabel(ratio) {
  if (ratio == null || Number.isNaN(ratio)) return null;
  const r = Number(ratio);
  if (r <= 0.5) return { label: "Underperforms", emoji: "📉", tint: "negative" };
  if (r <= 0.85) return { label: "Below average", emoji: "↘️", tint: "neutral" };
  if (r <= 1.05) return { label: "Typical", emoji: "➡️", tint: "muted" };
  if (r <= 1.4) return { label: "Slight boost", emoji: "↗️", tint: "positive" };
  if (r <= 2.5) return { label: "Strong boost", emoji: "🔥", tint: "positive" };
  if (r <= 5) return { label: "Major boost", emoji: "⭐", tint: "positive" };
  return { label: "Outstanding", emoji: "✨", tint: "positive" };
}

/**
 * Get color tint for opportunity label based on strategic value.
 */
export function getOpportunityLabelTint(label) {
  if (!label) return "muted";
  const positive = ["High Opportunity", "Possible Gem", "Rising"];
  const neutral = ["Established"];
  const negative = ["Saturated", "Declining"];
  if (positive.includes(label)) return "positive";
  if (negative.includes(label)) return "negative";
  return "neutral";
}

/** Human-readable label for pattern-brand fit score (0-100). */
export function getBrandFitLabel(score) {
  if (score == null || Number.isNaN(Number(score))) return null;
  const s = Number(score);
  if (s >= 85) return { label: "Exceptional fit", emoji: "✨", tint: "positive" };
  if (s >= 70) return { label: "Strong fit", emoji: "🔥", tint: "positive" };
  if (s >= 55) return { label: "Good fit", emoji: "👍", tint: "neutral" };
  if (s >= 40) return { label: "Fair fit", emoji: "➡️", tint: "neutral" };
  return { label: "Weak fit", emoji: "📉", tint: "negative" };
}

export function getValuePositionInDistribution(value, distribution) {
  if (value == null || !distribution?.length) return null;
  const n = distribution.length;
  if (value <= distribution[0].start) return 0;
  const last = distribution[n - 1];
  if (value >= last.end) return 100;
  for (let i = 0; i < n; i++) {
    const b = distribution[i];
    if (value < b.start) continue;
    if (i < n - 1 && value > b.end) continue;
    const span = b.end - b.start || 1;
    const fraction = Math.min(1, (value - b.start) / span);
    return ((i + fraction) / n) * 100;
  }
  return 100;
}

export function formatAnalysisTooltip(analysis) {
  if (!analysis) return null;
  const parts = [];
  if (analysis.description?.trim()) {
    parts.push(analysis.description.trim());
  }
  if (analysis.how_it_performed?.trim()) {
    parts.push(`Performance: ${analysis.how_it_performed.trim()}`);
  }
  const whyText = formatWhyForTooltip(analysis.why);
  if (whyText) {
    parts.push(`Why it works:\n${whyText}`);
  }
  return parts.length ? parts.join("\n\n") : null;
}

function formatWhyForTooltip(why) {
  if (!Array.isArray(why) || !why.length) return null;
  const lines = why.slice(0, 4).map((item) => {
    if (typeof item === "string") return item.trim();
    if (item && typeof item === "object") {
      const emoji = item.emoji ? `${item.emoji} ` : "";
      const reason = (item.reason || "").trim();
      return reason ? `${emoji}${reason}` : null;
    }
    return null;
  }).filter(Boolean);
  return lines.length ? lines.join("\n") : null;
}

export function getBucketIndexForValue(value, distribution) {
  if (value == null || !distribution?.length) return null;
  const n = distribution.length;
  for (let i = 0; i < n; i++) {
    const b = distribution[i];
    if (value < b.start) return i > 0 ? i - 1 : 0;
    if (value <= b.end) return i;
  }
  return n - 1;
}
